import type { PageResult } from '@jiaofa/api-types'
import type {
  DeliveryType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  RefundStatus,
} from '@jiaofa/constants'
import { request } from './request'

export interface AdminOrderItem {
  id: string
  productId: string
  productName: string
  specificationText?: string
  quantity: number
  unitPrice: number
  totalAmount: number
  image?: string
}

/** 门店位置（订单详情展示，自取订单用它兜底）。 */
export interface AdminStoreLocation {
  latitude?: number
  longitude?: number
  address?: string
}

/**
 * 收货目的地（订单详情返回，自取订单为 null）。
 *
 * 字段与后端 `AdminDestinationVO` 一一对应；地址快照可能来自历史数据，
 * 除 `address` 外的字段都按可空处理。
 */
export interface AdminDestination {
  contactName?: string
  phone?: string
  address?: string
  latitude?: number
  longitude?: number
  deliveryPointId?: string
  deliveryPointName?: string
  /** 后端生成的高德 marker URI，可直接打开导航。 */
  mapUrl?: string
}

export interface AdminOrder {
  id: string
  orderNo: string
  userId: string
  userNickname?: string
  userPhone?: string
  storeId: string
  storeName?: string
  status: OrderStatus
  paymentStatus: PaymentStatus
  paymentMethod?: PaymentMethod
  /** 是否实际退款：存在退款流水或状态为已退款；取消未退款为 false */
  refunded?: boolean
  deliveryType: DeliveryType
  /** 门店坐标；自取订单展示门店地址时用它。 */
  storeLocation?: AdminStoreLocation
  /** 收货目的地；自取订单为 null。列表接口不返回，仅详情/完结接口返回。 */
  deliveryAddress?: AdminDestination | null
  /** 配送距离（公里，来自配送单）。 */
  distanceKm?: string | null
  /** 配送单号（来自配送单）。 */
  deliveryNo?: string | null
  items: AdminOrderItem[]
  goodsAmount: number
  discountAmount: number
  deliveryFee: number
  payableAmount: number
  paidAmount: number
  pickupNo?: string
  remark?: string
  createdAt: string
}

/** 取消配送费入参。`reason` 可省略，最长 100 字。 */
export interface DeliveryFeeCancelPayload {
  reason?: string
}

/** 取消配送费结果（金额单位：分）。 */
export interface DeliveryFeeCancelResult {
  orderId: string
  /** `UNPAID_DIRECT` 未支付直接减免 / `PAID_REFUND` 已支付退差额 / `ALREADY_ZERO` 已是 0 */
  mode: 'UNPAID_DIRECT' | 'PAID_REFUND' | 'ALREADY_ZERO' | string
  deliveryFeeBefore?: number
  deliveryFeeAfter?: number
  payableAmountAfter?: number
  paidAmountAfter?: number
  /** 仅 `PAID_REFUND` 返回 */
  refundId?: string | null
  refundNo?: string | null
  /** `REFUNDING` / `REFUNDED` / `WAITING_SHARE_RETURN` */
  refundStatus?: string | null
}

type AdminOrderResponse = Omit<AdminOrder, 'refunded'> & {
  refunded?: unknown
}

export interface OrderListParams {
  page?: number
  pageSize?: number
  orderNo?: string
  phone?: string
  storeId?: string
  status?: OrderStatus | ''
  deliveryType?: DeliveryType | ''
}

export interface UpdateOrderStatusPayload {
  status: OrderStatus
  remark?: string
}

export interface AdminRefund {
  id: string
  refundNo: string
  orderId: string
  orderNo?: string
  userId: string
  amount: number
  reason?: string
  status: RefundStatus
  channel?: string
  createdAt: string
}

export interface RefundListParams {
  page?: number
  pageSize?: number
}

export interface RefundAuditPayload {
  result: 'APPROVED' | 'REJECTED'
  reason?: string
}

export interface WechatRefundStatusResult {
  refundNo: string
  orderNo: string
  refundStatus: RefundStatus
  wechatStatus: string
  wechatRefundId?: string
  message?: string
  refundedAt?: string
}

export const orderApiReady = true as const

const adaptAdminOrder = (raw: AdminOrderResponse): AdminOrder => ({
  ...raw,
  refunded: Boolean(raw.refunded),
})

export const fetchOrders = async (params: OrderListParams): Promise<PageResult<AdminOrder>> => {
  const result = await request<PageResult<AdminOrderResponse>>({
    url: '/orders',
    method: 'GET',
    params,
  })
  return {
    ...result,
    list: result.list.map(adaptAdminOrder),
  }
}

export const fetchOrderDetail = async (id: string): Promise<AdminOrder> =>
  adaptAdminOrder(await request<AdminOrderResponse>({
    url: `/orders/${id}`,
    method: 'GET',
  }))

export const updateOrderStatus = async (
  id: string,
  payload: UpdateOrderStatusPayload,
): Promise<void> =>
  request<void, UpdateOrderStatusPayload>({
    url: `/orders/${id}/status`,
    method: 'PUT',
    data: payload,
  })

export const fetchRefunds = async (params: RefundListParams): Promise<PageResult<AdminRefund>> =>
  request<PageResult<AdminRefund>>({
    url: '/refunds',
    method: 'GET',
    params,
  })

export const auditRefund = async (id: string, payload: RefundAuditPayload): Promise<void> =>
  request<void, RefundAuditPayload>({
    url: `/refunds/${id}/audit`,
    method: 'POST',
    data: payload,
  })

export const refreshWechatRefundStatus = async (id: string): Promise<WechatRefundStatusResult> =>
  request<WechatRefundStatusResult>({
    url: `/refunds/${id}/wechat-status`,
    method: 'GET',
  })

/**
 * 取消配送费（后端 `POST /orders/{id}/delivery-fee/cancel`）。
 *
 * - 未支付：先关微信预支付，再把配送费置 0 并下调应付金额，不产生退款单（`UNPAID_DIRECT`）；
 * - 已支付：生成一笔 `DELIVERY_FEE` 部分退款（`PAID_REFUND`），**不会**把订单改成已退款、不退券；
 * - 配送费已为 0：幂等返回 `ALREADY_ZERO`。
 *
 * 已支付订单后端额外校验 `order:refunds:edit`，无该权限会返回 403。
 */
export const cancelDeliveryFee = async (
  id: string,
  payload: DeliveryFeeCancelPayload = {},
): Promise<DeliveryFeeCancelResult> =>
  request<DeliveryFeeCancelResult, DeliveryFeeCancelPayload>({
    url: `/orders/${id}/delivery-fee/cancel`,
    method: 'POST',
    data: payload,
  })

/**
 * 重发微信退款（运维用，后端 `POST /refunds/{id}/retry-wechat`）。
 *
 * 仅对 `channel=WECHAT` 且状态为 `REFUNDING` 的退款单有效；后端重发前会先查微信实态，
 * 若仍在处理中会拒绝。成功返回空 `data`，失败返回可直接展示给运维的业务错误。
 */
export const retryWechatRefund = async (id: string): Promise<void> =>
  request<void>({
    url: `/refunds/${id}/retry-wechat`,
    method: 'POST',
  })
